import React, { useEffect, useMemo, useState } from "react";
import StepWrapper from "./StepWrapper";
import {
  Form,
  FormControl,
  InputGroup,
  ListGroup,
  Spinner,
} from "react-bootstrap";
import { getFullName } from "../../../../Modules/utils/fullname";
import api from "../../../../Services/Api/api";

const SelectOfficeEmployeeStep = ({
  data,
  onSubmit,
  totalSteps,
  isEditing = false,
  user,
}) => {
  const [offices, setOffices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  const [officeQuery, setOfficeQuery] = useState("");
  const [employeeQuery, setEmployeeQuery] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const [{ users: employees }, { users: officeUsers }] =
          await Promise.all([
            api.get("v1/head-office/user", {
              queryStr: JSON.stringify({
                where: { role: { $in: ["agent", "employee"] } },
                limit: 0,
              }),
            }),
            api.get("v1/head-office/user", {
              queryStr: JSON.stringify({
                where: { role: { $in: ["office"] } },
                limit: 0,
              }),
            }),
          ]);

        setEmployees(employees);
        setOffices([user, ...officeUsers]);
      } catch (e) {
        console.error("Failed to load offices/employees", e);
      } finally {
        setLoading(false);
      }
    };

    if (!isEditing) load();
  }, [isEditing]);

  const filteredOffices = useMemo(() => {
    if (!officeQuery) return offices;
    return offices.filter((o) =>
      getFullName(o).toLowerCase().includes(officeQuery.toLowerCase())
    );
  }, [officeQuery, offices]);

  const filteredEmployees = useMemo(() => {
    if (!data.office) return [];

    return employees
      .filter((e) => e.office === data.office._id)
      .filter((e) =>
        employeeQuery
          ? getFullName(e).toLowerCase().includes(employeeQuery.toLowerCase())
          : true
      );
  }, [employees, data.office, employeeQuery]);

  return (
    <StepWrapper
      step={data.step}
      total={totalSteps}
      disableNext={!data.agent || !data.office}
      onPrev={() => onSubmit({ step: data.step - 1 })}
      onNext={() => onSubmit({ step: data.step + 1 })}
    >
      {isEditing ? (
        <>
          <Form.Label>Selected Office</Form.Label>
          <ListGroup.Item action active={true}>
            {getFullName(data.office)}
          </ListGroup.Item>
          <Form.Label>Selected Employee/Field Executive</Form.Label>
          <ListGroup.Item action active={true}>
            {getFullName(data.agent)}
          </ListGroup.Item>
          <div style={{ fontSize: "10px", color: "#888", marginTop: "10px" }}>
            *Field Executive modification not allowed
          </div>
        </>
      ) : loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      ) : (
        <>
          <Form.Label>
            {data.office ? (
              <>
                Selected Office: <b>{getFullName(data.office)}</b>
              </>
            ) : (
              "Select Office"
            )}
          </Form.Label>

          <Form.Select
            className="mb-3"
            value={data.office?._id || ""}
            onChange={(e) => {
              const office = offices.find((o) => o._id === e.target.value);

              onSubmit({
                office,
                employee: null, // reset employee when office changes
              });
            }}
          >
            <option value="" disabled>
              -- Select Office --
            </option>

            {offices.map((office) => (
              <option key={office._id} value={office._id}>
                {getFullName(office)}
              </option>
            ))}
          </Form.Select>

          {data.office && (
            <>
              <Form.Label>
                {data.agent ? (
                  <>
                    Selected Employee: <b>{getFullName(data.agent)}</b>
                  </>
                ) : (
                  "Select Employee"
                )}
              </Form.Label>

              <InputGroup className="mb-2">
                <FormControl
                  placeholder="Search employee..."
                  value={employeeQuery}
                  onChange={(e) => setEmployeeQuery(e.target.value)}
                />
              </InputGroup>

              <ListGroup>
                {filteredEmployees.map((emp) => (
                  <ListGroup.Item
                    key={emp._id}
                    action
                    active={emp._id === data.agent?._id}
                    onClick={() =>
                      onSubmit({
                        agent: emp,
                      })
                    }
                  >
                    {getFullName(emp)}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
        </>
      )}
    </StepWrapper>
  );
};

export default SelectOfficeEmployeeStep;
